<script setup>
import { computed } from 'vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
} from 'chart.js';
import { useI18n } from 'vue-i18n';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

const { t } = useI18n();

const sortedTopics = computed(() => {
  const entries = Object.entries(props.data);
  return entries.sort((a, b) => b[1] - a[1]).slice(0, 10);
});

const chartData = computed(() => ({
  labels: sortedTopics.value.map(([topic]) => topic),
  datasets: [
    {
      label: t('CAPTAIN_ANALYTICS.CHART.MENTIONS'),
      data: sortedTopics.value.map(([, count]) => count),
      backgroundColor: 'rgb(59, 130, 246)',
      borderRadius: 4,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: {
        color: 'rgba(148, 163, 184, 0.1)',
      },
    },
    y: {
      grid: {
        display: false,
      },
    },
  },
};

const hasData = computed(() => Object.keys(props.data).length > 0);
</script>

<template>
  <div
    class="flex flex-col p-4 rounded-xl bg-n-solid-2 shadow outline-1 outline outline-n-container"
  >
    <h3 class="text-base font-medium text-n-slate-12 mb-4">
      {{ $t('CAPTAIN_ANALYTICS.TOP_TOPICS') }}
    </h3>
    <div v-if="isLoading" class="flex items-center justify-center h-64">
      <Spinner />
    </div>
    <div
      v-else-if="!hasData"
      class="flex items-center justify-center h-64 text-n-slate-11"
    >
      {{ $t('CAPTAIN_ANALYTICS.NO_DATA') }}
    </div>
    <div v-else class="h-64">
      <Bar :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>
